import * as base64 from 'base-64';
import isNil from 'lodash-es/isNil';
import { useCallback, useMemo } from 'react';
export const pathParamsParsers = {
    string: (x) => x,
    number: (x) => {
        if (x === 'undefined') {
            console.warn('Got an "undefined" string value, probably an error, casting to undefined');
            return undefined;
        }
        else if (!isNil(x)) {
            const parsed = parseFloat(x);
            if (isNaN(parsed))
                return x;
            else
                return parsed;
        }
        else
            return x;
    }
};
export const parseParams = (rawParams, paramsParserMap) => Object.entries(rawParams).reduce((acc, [key, value]) => {
    const parser = paramsParserMap[key];
    if (isNil(parser))
        console.warn(`paramsParserMap for '${key}' param is undefined, probably wrong`);
    else {
        acc[key] = parser(value);
    }
    return acc;
}, {});
// Search params
function decodeFromBinary(str) {
    return decodeURIComponent(Array.prototype.map
        .call(base64.decode(str), function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    })
        .join(''));
}
function encodeToBinary(str) {
    return base64.encode(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) => String.fromCharCode(parseInt(p1, 16))));
}
function validateSearchParams(schema, parsedSearchParams) {
    let validated = parsedSearchParams;
    if (schema) {
        const schemaValidation = schema.safeParse(parsedSearchParams);
        if (schemaValidation.success) {
            validated = schemaValidation.data;
        }
        else {
            // eslint-disable-next-line no-console
            console.warn(`Failed to validate zod schema, got ${schemaValidation.error} for ${JSON.stringify(parsedSearchParams, null, 2)}`);
        }
    }
    return validated;
}
export var SearchParamsEncoding;
(function (SearchParamsEncoding) {
    SearchParamsEncoding[SearchParamsEncoding["Base64"] = 0] = "Base64";
    SearchParamsEncoding[SearchParamsEncoding["Json"] = 1] = "Json";
})(SearchParamsEncoding || (SearchParamsEncoding = {}));
function parseSearchParams(urlParams, encoding) {
    const parsedSearchParams = {};
    for (const [key, rawValue] of urlParams.entries()) {
        let decoded = encoding === SearchParamsEncoding.Base64 ? undefined : rawValue;
        if (encoding === SearchParamsEncoding.Base64) {
            try {
                decoded = decodeFromBinary(rawValue);
            }
            catch (error) {
                console.log(`Failed to decodeFromBinary '${key}' search param, will default to raw value: ${rawValue}`);
                parsedSearchParams[key] = rawValue;
            }
        }
        if (decoded !== 'undefined' && decoded !== undefined) {
            try {
                if (decoded.includes('{') || decoded.includes('[')) {
                    parsedSearchParams[key] = JSON.parse(decoded);
                }
                else {
                    parsedSearchParams[key] = rawValue.replace(/"/g, '').replace(/'/, '');
                }
            }
            catch (error) {
                console.log(`Failed to JSON.parse '${key}' search param with decoded value: ${decoded},  will default to raw value: ${rawValue}`);
                parsedSearchParams[key] = rawValue;
            }
        }
    }
    return parsedSearchParams;
}
const stringifySearchParams = (params, encoding) => {
    const urlParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) {
            const jsonized = JSON.stringify(value);
            urlParams.set(key, encoding === SearchParamsEncoding.Base64 ? encodeToBinary(jsonized) : jsonized);
        }
    }
    return urlParams.toString();
};
export const joinToAndSearchParams = (to, search, encoding) => {
    return `${to}${!isNil(search) && Object.keys(search).length > 0
        ? '?' + stringifySearchParams(search, encoding)
        : ''}`;
};
export const makeUseSearchParams = (encoding, 
/** @see This hook is considered static (should not change on the fly, not inside memoization deps) */
useSearchParamsHook) => {
    const useSearchParams = (schema) => {
        const [urlParams, setUrlParams] = useSearchParamsHook();
        const urlParamsString = urlParams.toString();
        const parsedSearchParams = useMemo(() => parseSearchParams(urlParams, encoding), [urlParamsString, encoding]);
        const validatedParams = useMemo(() => validateSearchParams(schema, parsedSearchParams), [parsedSearchParams]);
        const setParams = useCallback((params) => setUrlParams(stringifySearchParams(params, encoding)), [encoding]);
        const useSearchParamsHookResult = useMemo(() => [validatedParams, setParams], [validatedParams, setParams]);
        return useSearchParamsHookResult;
    };
    return useSearchParams;
};
export function urlSearchParamsFromObj(obj) {
    const urlSearchParams = new URLSearchParams();
    Object.keys(obj).forEach((key) => {
        urlSearchParams.append(key, obj[key]);
    });
    return urlSearchParams;
}
export function urlSearchParamsToObj(urlSearchParams) {
    const obj = {};
    for (const key of urlSearchParams.keys()) {
        obj[key] = urlSearchParams.get(key);
    }
    return obj;
}
export const makeResolvePathPattern = (pathPatternResolver) => (pathname) => {
    let currentResolver = pathPatternResolver;
    const pathnameSplit = pathname.replace('/', '').split('/');
    return ('/' +
        pathnameSplit
            .reduce((acc, pathnameSegment) => {
            const nestedResolver = currentResolver === true ? undefined : currentResolver[pathnameSegment];
            if (nestedResolver === undefined) {
                acc.push('*');
                if (currentResolver !== true) {
                    currentResolver = currentResolver['*'];
                }
            }
            else {
                acc.push(pathnameSegment);
                currentResolver = nestedResolver;
            }
            return acc;
        }, [])
            .join('/'));
};
export const matchPath = (pathPattern, pathname) => {
    if (!pathPattern.startsWith('/')) {
        throw new Error(`matchPath with a relative pathname is not supported for now, give me an absolute path starting with "/"`);
    }
    const endsWithAsterisk = pathPattern.endsWith('/*');
    // removing /* if it exists
    let testedPattern = endsWithAsterisk ? pathPattern.slice(0, -2) : pathPattern;
    if (testedPattern === '') {
        testedPattern = '/';
    }
    // First item will be an empty string since pathname start with a /
    const [, ...pathnameSegments] = pathname.split('/');
    const [, ...patternSegments] = testedPattern.split('/');
    // if the given pattern has more segments than the pathname then a match is impossible
    if (patternSegments.length > pathnameSegments.length)
        return null;
    // Building a pathname from the testedPattern to test against the current pathname
    // Replacing dynamic params with there current value from the given pathname
    const interpolatedPathnameBase = testedPattern === '/'
        ? testedPattern
        : '/' +
            patternSegments
                .map((segment, i) => {
                if (segment.startsWith(':')) {
                    throw new Error(`Can not use a dynamic route segment (path param '${segment}') to match path, use '*'`);
                }
                // if the segment from the pattern is dynamic
                else if (segment === '*') {
                    return pathnameSegments[i];
                }
                else {
                    return segment;
                }
            })
                .join('/');
    const patternMatchPathname = endsWithAsterisk
        ? pathname.startsWith(interpolatedPathnameBase +
            // This is very important, prove there is another segment since trailing slash are not allowed
            '/'
        // And we also want to match if it is completely equal
        ) || pathname === interpolatedPathnameBase
        : pathname === interpolatedPathnameBase;
    if (patternMatchPathname) {
        return { pattern: pathPattern, pathname, pathnameBase: testedPattern };
    }
    else
        return null;
};
